[
  {
    "id": "unit-01",
    "week": 1,
    "title": "Launch the Platformer",
    "pythonFocus": "Data types, statements, arithmetic, input, and output",
    "gameFeature": "Open a Pygame window and plan the player",
    "published": true,
    "summary": "Meet the platform-game project, learn the build-test-debug routine, and create the first game window.",
    "objectives": [
      "Explain how a game loop updates a game",
      "Use print statements and variables",
      "Run and save a Python file"
    ],
    "vocabulary": [
      "statement",
      "variable",
      "data type",
      "coordinate",
      "game loop"
    ],
    "studentSteps": [
      "Name the platform game and sketch its goal",
      "Create a blank main.py file",
      "Copy the smartboard demonstration",
      "Change the window title and size",
      "Save the first working checkpoint"
    ],
    "assignment": "Create a window for an original platform game and add a short comment block describing the player, goal, and controls."
  },
  {
    "id": "unit-02",
    "week": 2,
    "title": "Move the Player",
    "pythonFocus": "Control statements and Boolean operators",
    "gameFeature": "Keyboard movement and screen boundaries",
    "published": true,
    "summary": "Use decisions and keyboard input to move a player rectangle left and right.",
    "objectives": [
      "Use if statements for keyboard input",
      "Combine Boolean conditions",
      "Keep the player inside the window"
    ],
    "vocabulary": [
      "Boolean",
      "condition",
      "if",
      "operator",
      "velocity"
    ],
    "studentSteps": [
      "Copy the player rectangle and speed variables",
      "Read the keyboard state",
      "Add left and right decisions",
      "Add boundary checks",
      "Test both controls"
    ],
    "assignment": "Add smooth left/right movement and prevent the player from leaving the game window."
  },
  {
    "id": "unit-03",
    "week": 3,
    "title": "Track Game Data",
    "pythonFocus": "Introductory data science and game statistics",
    "gameFeature": "Score, health, attempts, and a heads-up display",
    "published": true,
    "summary": "Collect meaningful game data and display it while the platformer runs.",
    "objectives": [
      "Choose useful game measures",
      "Update numeric variables",
      "Display formatted game information"
    ],
    "vocabulary": [
      "metric",
      "score",
      "health",
      "counter",
      "data"
    ],
    "studentSteps": [
      "Choose three game statistics",
      "Create starting values",
      "Update a value after an event",
      "Draw the values on screen",
      "Explain what the data tells the player"
    ],
    "assignment": "Add a readable heads-up display containing at least score, health, or attempts."
  },
  {
    "id": "unit-04",
    "week": 4,
    "title": "Build with Functions",
    "pythonFocus": "Functions, random numbers, and the math module",
    "gameFeature": "Reusable drawing and random collectible placement",
    "published": true,
    "summary": "Turn repeated work into functions and use randomness to make the level change.",
    "objectives": [
      "Define and call a function",
      "Use parameters",
      "Generate a safe random position"
    ],
    "vocabulary": [
      "function",
      "parameter",
      "argument",
      "return",
      "random"
    ],
    "studentSteps": [
      "Find repeated code",
      "Create one drawing function",
      "Pass values into the function",
      "Randomize a collectible position",
      "Call the function from the game loop"
    ],
    "assignment": "Create at least two useful functions and one randomized game event."
  },
  {
    "id": "unit-05",
    "week": 5,
    "title": "Gravity and Jumping",
    "pythonFocus": "Scope, arguments, return values, and Exam 1 review",
    "gameFeature": "Gravity, jumping, and organized update functions",
    "published": true,
    "summary": "Use functions and changing velocity to make the player jump and fall.",
    "objectives": [
      "Explain local and global scope",
      "Pass changing values into functions",
      "Apply gravity each frame"
    ],
    "vocabulary": [
      "scope",
      "argument",
      "return value",
      "gravity",
      "acceleration"
    ],
    "studentSteps": [
      "Create vertical velocity",
      "Add gravity each frame",
      "Move the player vertically",
      "Start a jump only on the ground",
      "Refactor the logic into a function"
    ],
    "assignment": "Add a controlled jump and gravity without using copied starter code."
  },
  {
    "id": "unit-06",
    "week": 6,
    "title": "Create Platforms with Lists",
    "pythonFocus": "Lists and tuples",
    "gameFeature": "A level containing many platforms",
    "published": true,
    "summary": "Store platforms as a collection instead of creating each one with separate variables.",
    "objectives": [
      "Create and traverse a list",
      "Use tuples for fixed coordinates",
      "Generate rectangles from level data"
    ],
    "vocabulary": [
      "list",
      "tuple",
      "index",
      "iterate",
      "collection"
    ],
    "studentSteps": [
      "Write platform coordinate tuples",
      "Store them in a list",
      "Loop through the list",
      "Create and draw every platform",
      "Add an original platform arrangement"
    ],
    "assignment": "Build a level with at least eight platforms stored in a list."
  },
  {
    "id": "unit-07",
    "week": 7,
    "title": "Design Levels and Search Data",
    "pythonFocus": "Sorting, searching, and two-dimensional lists",
    "gameFeature": "Tile map and level layout",
    "published": true,
    "summary": "Represent a complete level as rows and columns, then search it for game objects.",
    "objectives": [
      "Read a two-dimensional list",
      "Use nested loops",
      "Search for special tile values"
    ],
    "vocabulary": [
      "two-dimensional list",
      "row",
      "column",
      "search",
      "tile"
    ],
    "studentSteps": [
      "Create a small tile map",
      "Use nested loops",
      "Draw solid tiles",
      "Locate the player-start tile",
      "Revise the map for fair difficulty"
    ],
    "assignment": "Create an original tile-map level with a start, platforms, and a goal."
  },
  {
    "id": "unit-08",
    "week": 8,
    "title": "Organize Game Settings",
    "pythonFocus": "Dictionaries and sets",
    "gameFeature": "Player attributes, settings, and collected-item tracking",
    "published": true,
    "summary": "Use dictionaries for named settings and sets for unique collected items.",
    "objectives": [
      "Store key-value pairs",
      "Use a set to prevent duplicates",
      "Choose the right collection type"
    ],
    "vocabulary": [
      "dictionary",
      "key",
      "value",
      "set",
      "unique"
    ],
    "studentSteps": [
      "Move player stats into a dictionary",
      "Create a settings dictionary",
      "Track collected item IDs in a set",
      "Update one value during play",
      "Print the final data for testing"
    ],
    "assignment": "Use one dictionary and one set to manage the platformer."
  },
  {
    "id": "unit-09",
    "week": 9,
    "title": "Collision Systems",
    "pythonFocus": "Comparing sets and set operations",
    "gameFeature": "Reliable player-platform and collectible collisions",
    "published": true,
    "summary": "Compare groups of active objects and build predictable collision rules.",
    "objectives": [
      "Use intersection and difference",
      "Detect rectangle collisions",
      "Separate horizontal and vertical collision responses"
    ],
    "vocabulary": [
      "intersection",
      "difference",
      "collision",
      "mask",
      "hitbox"
    ],
    "studentSteps": [
      "Identify active and collected objects",
      "Compare their ID sets",
      "Detect player-platform contact",
      "Correct the player position",
      "Test edge cases"
    ],
    "assignment": "Make platform landing and collectible collision reliable across the level."
  },
  {
    "id": "unit-10",
    "week": 10,
    "title": "Scrolling World",
    "pythonFocus": "Set operations and Exam 2 review",
    "gameFeature": "Camera scrolling and a larger level",
    "published": true,
    "summary": "Move the world around the player and review collection concepts through a larger level.",
    "objectives": [
      "Distinguish world and screen coordinates",
      "Apply a camera offset",
      "Review lists, dictionaries, and sets"
    ],
    "vocabulary": [
      "camera",
      "offset",
      "world coordinate",
      "screen coordinate",
      "viewport"
    ],
    "studentSteps": [
      "Create a camera_x variable",
      "Follow the player after mid-screen",
      "Subtract the camera offset while drawing",
      "Extend the level width",
      "Complete the Exam 2 review check"
    ],
    "assignment": "Create a scrolling level wider than the game window."
  },
  {
    "id": "unit-11",
    "week": 11,
    "title": "Build Level Arrays",
    "pythonFocus": "Arrays created from existing data",
    "gameFeature": "Convert level data into reusable numeric arrays",
    "published": true,
    "summary": "Transform collected level information into arrays that support progression and balancing.",
    "objectives": [
      "Create an array from existing data",
      "Inspect array shape and values",
      "Use numeric data to tune a level"
    ],
    "vocabulary": [
      "array",
      "shape",
      "element",
      "numeric data",
      "conversion"
    ],
    "studentSteps": [
      "Collect platform heights or completion times",
      "Create an array",
      "Inspect its values",
      "Calculate one useful measure",
      "Adjust one level feature from the result"
    ],
    "assignment": "Create an array from platformer data and use it to justify one design improvement."
  },
  {
    "id": "unit-12",
    "week": 12,
    "title": "Generate Difficulty Data",
    "pythonFocus": "Arrays created from ranges",
    "gameFeature": "Level progression and increasing challenge",
    "published": true,
    "summary": "Generate predictable numeric patterns for levels, enemies, or rewards.",
    "objectives": [
      "Create arrays from ranges",
      "Apply a numeric pattern",
      "Use generated data in gameplay"
    ],
    "vocabulary": [
      "range",
      "sequence",
      "progression",
      "difficulty curve",
      "broadcasting"
    ],
    "studentSteps": [
      "Generate level numbers",
      "Generate a matching difficulty pattern",
      "Connect one value to each level",
      "Display the current level",
      "Test for a fair increase"
    ],
    "assignment": "Use a generated range to control difficulty across at least five level stages."
  },
  {
    "id": "unit-13",
    "week": 13,
    "title": "Analyze Playtest Scores",
    "pythonFocus": "NumPy calculation methods",
    "gameFeature": "Playtesting and data-driven balancing",
    "published": true,
    "summary": "Use playtest results to find typical performance and tune the platformer.",
    "objectives": [
      "Calculate summary statistics",
      "Interpret the result",
      "Revise a game using evidence"
    ],
    "vocabulary": [
      "mean",
      "median",
      "minimum",
      "maximum",
      "playtest"
    ],
    "studentSteps": [
      "Collect at least five playtest results",
      "Create a NumPy array",
      "Calculate summary statistics",
      "Identify one problem",
      "Revise and retest"
    ],
    "assignment": "Submit before-and-after playtest data and explain one balancing change."
  },
  {
    "id": "unit-14",
    "week": 14,
    "title": "Create a Game Data Report",
    "pythonFocus": "Pandas Series and DataFrames",
    "gameFeature": "Player statistics, test results, sound, and polish",
    "published": true,
    "summary": "Organize playtest information into a table and use it to plan final polish.",
    "objectives": [
      "Create a DataFrame",
      "Select and summarize columns",
      "Connect findings to game design"
    ],
    "vocabulary": [
      "Series",
      "DataFrame",
      "column",
      "row",
      "summary"
    ],
    "studentSteps": [
      "Create a playtest table",
      "Add player, score, time, and result",
      "Summarize numeric columns",
      "Choose a polish priority",
      "Add sound or visual feedback"
    ],
    "assignment": "Create a small playtest DataFrame and complete one evidence-based polish improvement."
  },
  {
    "id": "unit-15",
    "week": 15,
    "title": "Publish the Final Platformer",
    "pythonFocus": "String formatting and string operations",
    "gameFeature": "Menus, instructions, scoreboard, credits, and final presentation",
    "published": true,
    "summary": "Finish the player experience, complete testing, and present the semester platform game.",
    "objectives": [
      "Format readable game text",
      "Create start and end screens",
      "Test and present a complete project"
    ],
    "vocabulary": [
      "formatted string",
      "menu",
      "state",
      "credits",
      "release"
    ],
    "studentSteps": [
      "Create title and instruction screens",
      "Format score and status text",
      "Add a win or game-over screen",
      "Complete the final test checklist",
      "Submit the code, reflection, and presentation"
    ],
    "assignment": "Publish and present a complete original platformer with instructions, gameplay, scoring, and a clear ending."
  }
]
